// client/src/pages/seo/SpeisekarteUbersetzenRestaurantDe.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";
import CountryBanner from "../../components/CountryBanner.jsx";

const sections = [
  {
    id: "start",
    label: "Speisekarte übersetzen sollte einfach bleiben",
  },
  {
    id: "paper",
    label: "Gedruckte Speisekarten in mehreren Sprachen",
  },
  {
    id: "ai",
    label: "Speisekarte mit KI übersetzen",
  },
  {
    id: "localization",
    label: "Lokalisierung der Restaurant-Speisekarte",
  },
  {
    id: "comparison",
    label: "Vorteile und Nachteile jeder Option",
  },
  {
    id: "recommendation",
    label: "Welche Lösung sollte ein Restaurantbesitzer wählen?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place bei übersetzten Menüs hilft",
  },
  {
    id: "faq",
    label: "FAQ zur Übersetzung von Speisekarten",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für eine Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function setMetaDescription(content) {
  if (typeof document === "undefined") return;

  let meta = document.querySelector('meta[name="description"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

export default function SpeisekarteUbersetzenRestaurantDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");

    document.title =
      "Speisekarte übersetzen für Restaurants: KI, Papier oder QR-Menü? | Best Place";

    setMetaDescription(
      "Erfahren Sie, wie Restaurants ihre Speisekarte übersetzen können: Papiermenüs, KI-Übersetzung, Zutatenübersetzung, Lokalisierung und mehrsprachige QR-Menüs."
    );
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          de: "/de/speisekarte-ubersetzen-restaurant",
          en: "/en/menu-translation-for-restaurants",
          no: "/no/oversette-restaurantmeny",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Speisekarte übersetzen: Papier, KI oder digitales QR-Menü?
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Der größte Schmerzpunkt für viele ausländische Gäste im
                Restaurant ist die Speisekarte als Text. Wenn Sie eine Lösung für
                Ihr Restaurant suchen, lesen Sie diesen Artikel bis zum Ende. Sie
                finden sicher eine passende Richtung für Ihren Betrieb.
              </p>

              <p className="seoHero__lead">
                Eine Restaurant-Speisekarte zu übersetzen klingt einfach. Text
                nehmen, übersetzen, veröffentlichen. Fertig. In einem Restaurant
                ist es selten so einfach.
              </p>

              <p className="seoHero__text">
                Gerichte haben Zutaten. Manche Namen sind lokal. Allergene sind
                wichtig. Gäste möchten verstehen, was sie bestellen. Eine gute
                Übersetzung macht die Speisekarte klarer. Nicht komplizierter.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="countryBannerBlock">
        <CountryBanner
          reverse
          ariaLabel={"Countries and languages"}
        />
      </div>

      <SeoContactSection
        lang="de"
        apiOrigin={CALLBACK_API_ORIGIN}
        title="Brauchen Sie Hilfe mit einer übersetzten Speisekarte?"
      />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>Speisekarte übersetzen sollte einfach bleiben</h2>

          <p>
            Viele Restaurantbesitzer denken über Übersetzung nach, wenn Gäste zu
            viele Fragen stellen. Der Gast versteht ein Gericht nicht. Das Team
            erklärt immer wieder dasselbe. Irgendwann wird klar: Die Speisekarte
            braucht eine weitere Sprache.
          </p>

          <p>
            Dafür gibt es mehrere Ansätze. Sie können eine gedruckte Speisekarte
            in mehreren Sprachen nutzen, KI-Übersetzungsdienste verwenden oder
            eine digitale{" "}
            <Link
              to="/de/mehrsprachige-speisekarte"
              className="seoAccentLink"
            >
              mehrsprachige Speisekarte für Restaurants
            </Link>{" "}
            mit Übersetzungsmöglichkeiten einsetzen.
          </p>

          <p>
            Nicht jede Lösung passt zu jedem Restaurant. Ein kleines Café braucht
            vielleicht nur Englisch. Ein Restaurant in der Nähe von Hotels
            braucht mehrere Sprachen. Ein touristischer Standort braucht
            vielleicht noch mehr. Ziel ist nicht die maximale Anzahl an Sprachen.
            Ziel ist, die richtigen Sprachen für echte Gäste auszuwählen.
          </p>
        </section>

        <section id="paper" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Gedruckte Speisekarten in mehreren Sprachen</h2>

            <p>
              Eine klassische Papier-Speisekarte gehört in vielen Restaurants
              einfach dazu. Gäste sind daran gewöhnt. Aber sie zu übersetzen kann
              eine echte Herausforderung sein. Es klingt zuerst leicht: Man
              druckt die Speisekarte in zwei oder drei Sprachen und fertig. Für
              manche Restaurants reicht das. Das Team muss nichts erklären. Aber
              schon eine zusätzliche Sprache verdoppelt den Umfang der
              Speisekarte. Wenn zwei oder drei Sprachen nötig sind, wird die
              Aufgabe schnell sehr schwer.
            </p>

            <p>
              Es gibt noch eine andere Möglichkeit, und meiner Meinung nach ist
              sie gar nicht schlecht. Man kann mehrere Speisekarten in
              verschiedenen Sprachen haben. Wenn ein italienischer Gast kommt,
              bekommt er die italienische Version. Wenn ein deutscher Gast kommt,
              bekommt er die deutsche Version. Für Gäste ist das ideal: Sie
              erhalten einfach ein Menü in ihrer Sprache. Trotzdem muss man
              verstehen, dass die Schwierigkeiten deutlich wachsen, wenn:
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                mehrere Sprachen gedruckte Menüs länger machen.
              </li>
              <li className="text__seo-li">
                Aktualisierungen Zeit und Druckkosten verursachen.
              </li>
              <li className="text__seo-li">
                kleine Schrift schnell schwer lesbar wird.
              </li>
              <li className="text__seo-li">
                alte Übersetzungen oft zu lange im Umlauf bleiben.
              </li>
              <li className="text__seo-li">
                Allergene und Zutaten in jeder Sprache sorgfältig geprüft werden
                müssen.
              </li>
            </ul>

            <p>
              Papier ist nicht falsch. Es ist nur nicht flexibel. Es funktioniert
              am besten bei kurzen Speisekarten, die sich selten ändern.
            </p>
          </div>
        </section>

        <section id="ai" className="acquaintance">
          <h2>Speisekarte mit KI übersetzen</h2>

          <p>
            Wir haben uns alle daran gewöhnt, dass das Übersetzen von Texten mit
            künstlicher Intelligenz unglaublich einfach geworden ist. Bei der
            Übersetzung einer Restaurant-Speisekarte ist es aber nicht ganz so
            eindeutig. KI kann schnell einen Rohentwurf erstellen. Sie kann
            einfache Beschreibungen schreiben. Schwierigkeiten entstehen meistens
            bei nationalen Gerichten, regionalen Spezialitäten oder neuen
            Produkten. Besonders interessant ist die automatische Übersetzung von
            Zutaten. Genau dort können aber viele Bedeutungen und Nuancen
            entstehen.
          </p>

          <p>
            Die Formel für KI-gestützte Menüübersetzung ist im Grunde einfach.
            Ich würde KI für den ersten Entwurf nutzen. Danach sollten die Namen
            der Gerichte, Zutaten, Allergene und regionalen Besonderheiten
            geprüft werden. Erst dann veröffentlichen.
          </p>

          <p>
            Es gibt auch KI-Übersetzungsdienste, mit denen Restaurantmenüs online
            angezeigt werden können. Diese Dienste erlauben Gästen, eine Sprache
            im System auszuwählen, und die KI übersetzt den Inhalt automatisch.
          </p>

          <VisualIdea>
            Kurz gesagt: Eine Speisekarte mit KI zu übersetzen ist schnell und
            ziemlich einfach. Man sollte aber beachten, dass manche Textteile in
            der Sprache des Gastes nicht korrekt oder nicht natürlich übersetzt
            werden.
          </VisualIdea>
        </section>

        <section id="localization" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Lokalisierung der Restaurant-Speisekarte</h2>

            <p>
              Wenn die Übersetzung Ihrer Restaurant-Speisekarte mehr sein soll
              als ein einfacher Wortwechsel, braucht sie besondere Aufmerksamkeit.
              Eine gute Menüübersetzung ist Lokalisierung. Gäste sollten
              verstehen, was sie probieren möchten, auch wenn sie mit der lokalen
              Küche nicht vertraut sind.
            </p>

            <p>
              Beispiel: Ein regionaler Gerichtsname kann für lokale Gäste sofort
              klar sein. Für Touristen bedeutet er vielleicht nichts. In diesem
              Fall ist eine kurze Beschreibung besser als eine direkte
              Übersetzung. Was ist drin? Wie wird es serviert? Ist es scharf? Ist
              es vegetarisch? Gibt es Allergene?
            </p>

            <p>
              Es gibt Dienste, die eine hochwertige Übersetzung von
              Restaurantmenüs übernehmen. Das ist ein recht umfangreicher Prozess,
              weil dabei passende Fachleute beteiligt sein sollten, zum Beispiel
              Linguisten oder Übersetzer mit Verständnis für Gastronomie.
            </p>
          </div>
        </section>

        <section id="comparison" className="acquaintance">
          <h2>Vorteile und Nachteile jeder Option</h2>

          <p>
            Es gibt keine perfekte Lösung für jedes Restaurant. Die richtige Wahl
            hängt davon ab, wie oft sich die Speisekarte ändert und wie viele
            internationale Gäste kommen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Schwächer bei Aktualisierungen</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Papiermenüs müssen nach Änderungen neu gedruckt werden.
                </li>
                <li className="text__seo-li">
                  Kamera-Apps geben dem Restaurant keine Kontrolle.
                </li>
                <li className="text__seo-li">
                  KI-Übersetzungen müssen geprüft werden.
                </li>
                <li className="text__seo-li">
                  Einzelne PDF-Dateien werden schwer zu verwalten.
                </li>
                <li className="text__seo-li">
                  Mehrere Sprachversionen aktuell zu halten ist aufwendig.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besser für flexible Restaurants</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Ein digitales Menü kann mehrere Sprachen enthalten.
                </li>
                <li className="text__seo-li">
                  Gäste wählen die Sprache auf dem Smartphone.
                </li>
                <li className="text__seo-li">
                  Preise und Beschreibungen lassen sich online aktualisieren.
                </li>
                <li className="text__seo-li">
                  Zutaten und Allergene können pro Sprache geprüft werden.
                </li>
                <li className="text__seo-li">
                  Derselbe QR-Code kann nach Änderungen weiter genutzt werden.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="recommendation" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Lösung sollte ein Restaurantbesitzer wählen?</h2>

            <p>
              Wenn sich Ihre Speisekarte fast nie ändert und Sie nur eine zweite
              Sprache brauchen, kann ein gedrucktes Menü ausreichen. Dann halten
              Sie es einfach.
            </p>

            <p>
              Wenn Ihr Restaurant oft Preise, Gerichte oder Tagesangebote
              ändert, ist ein digitales Menü meistens praktischer. Sie müssen
              nicht jedes Mal neu drucken.
            </p>

            <p>
              Wenn Sie Touristen, Hotelgäste oder internationale Besucher
              bedienen, starten Sie mit den Sprachen, die Sie wirklich in Ihrem
              Restaurant hören. Englisch ist oft der erste Schritt. Danach kommt
              die nächste Sprache nach echter Nachfrage.
            </p>

            <p>
              Ein guter nächster Schritt ist ein Test mit einem{" "}
              <Link
                to="/de/mehrsprachige-speisekarte"
                className="seoAccentLink"
              >
                mehrsprachigen QR-Menü
              </Link>
              . Prüfen Sie es auf dem Smartphone. Fragen Sie das Team. Beobachten
              Sie, ob Gäste Gerichte schneller verstehen. Danach können Sie
              erweitern.
            </p>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section className="acquaintance">
          <h2>Wie Best Place bei übersetzten Menüs hilft</h2>

          <p>
            Best Place hilft Restaurantbesitzern, eine digitale Speisekarte in
            mehreren Sprachen zu erstellen. Das Restaurant behält eine klare
            Struktur. Der Gast öffnet das Menü per QR-Code und wählt die Sprache,
            die er braucht.
          </p>

          <p>
            Das ist nützlich, wenn ein Restaurant professioneller auf
            internationale Gäste wirken, wiederholte Fragen reduzieren und die
            Speisekarte ohne neue Druckdaten aktualisieren möchte.
          </p>

          <p>
            Sie können über die{" "}
            <Link to="/de" className="seoAccentLink">
              Best Place Startseite
            </Link>{" "}
            beginnen, ein einfaches Menü erstellen und es direkt auf einem echten
            Smartphone testen.
          </p>
        </section>

        <section id="faq" className="acquaintance">
          <h2>FAQ zur Übersetzung von Speisekarten</h2>

          <div className="seoFaq">
            <details>
              <summary>Was bedeutet Übersetzung einer Restaurant-Speisekarte?</summary>

              <p>
                Es bedeutet, Gerichtsnamen, Beschreibungen, Kategorien, Preise,
                Zutaten und wichtige Hinweise in eine andere Sprache zu
                übertragen, damit Gäste das Menü leichter verstehen.
              </p>
            </details>

            <details>
              <summary>
                Wie übersetze ich eine Café-Speisekarte richtig ins Englische?
              </summary>

              <p>
                Starten Sie mit den wichtigsten Positionen. Übersetzen Sie nicht
                nur Namen. Ergänzen Sie kurze Beschreibungen, Zutaten, Größen,
                Milchoptionen, Allergene und Extras, wenn sie dem Gast bei der
                Entscheidung helfen.
              </p>
            </details>

            <details>
              <summary>
                Kann KI Zutaten und Gerichte automatisch übersetzen?
              </summary>

              <p>
                Ja, KI kann einen ersten Entwurf erstellen. Zutaten, Allergene,
                regionale Begriffe und sensible Hinweise sollten aber immer
                geprüft werden.
              </p>
            </details>

            <details>
              <summary>
                Was bedeutet Lokalisierung einer Restaurant-Speisekarte?
              </summary>

              <p>
                Lokalisierung bedeutet, dass die Speisekarte nicht nur Wort für
                Wort übersetzt wird. Die Beschreibung wird so angepasst, dass
                Gäste aus anderen Ländern das Gericht wirklich verstehen.
              </p>
            </details>

            <details>
              <summary>
                Ist ein mehrsprachiges QR-Menü besser als mehrere PDFs?
              </summary>

              <p>
                In vielen Fällen ja. Ein mehrsprachiges QR-Menü ist einfacher zu
                aktualisieren, auf dem Smartphone sauberer und flexibler als
                mehrere einzelne PDF-Dateien.
              </p>
            </details>

            <details>
              <summary>
                Welche Sprache sollte ein Restaurant zuerst hinzufügen?
              </summary>

              <p>
                Starten Sie mit der Sprache, die Ihre internationalen Gäste am
                häufigsten nutzen. Für viele Restaurants ist Englisch die erste
                sinnvolle Zusatzsprache.
              </p>
            </details>
          </div>
        </section>
      </article>
    </main>
  );
}